import React, { useRef } from 'react';
import { css } from '@emotion/react';
import uuid from 'react-uuid';
import ClickableContainer, { ClickableContainerProps } from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { black, white } from 'styles/global_defaults/colors';

type Props = {
  color1: string;
  color2: string;
  color3: string;
  icon?: string;
} & Omit<ClickableContainerProps, 'children'>;

const iconOverlayStyles = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

// novoed-icons has no pause glyph, so the two bars are drawn manually
const pauseBarsStyles = css`
  ${iconOverlayStyles};
  display: flex;
  gap: 3px;

  span {
    width: 3px;
    height: 12px;
    border-radius: 1px;
    background-color: ${white};
  }
`;

// forwardRef so NvTooltip's OverlayTrigger can anchor to the rendered element
const VoiceCircleColor = React.forwardRef<HTMLDivElement, Props>(({ color1, color2, color3, icon, ...clickableProps }, ref) => {
  const id = useRef(uuid()).current;
  const gradientId = `${id}-gradient`;
  const clip0Id = `${id}-clip0`;
  const clip1Id = `${id}-clip1`;

  const gradientColor1 = icon ? black : color1;
  const gradientColor2 = icon ? '#FFFFFF80' : color2;
  const gradientColor3 = icon ? '#FFFFFF59' : color3;

  return (
    <ClickableContainer {...clickableProps} ref={ref} layoutOnly={!clickableProps.onClick}>
      <div css={css`position: relative; display: inline-flex; ${icon && `background: ${black}; border-radius: 50%;`}`}>
        <svg width={32} height={32} viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <g clipPath={`url(#${clip0Id})`}>
            <path d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z' fill={`url(#${gradientId})`} />
            <g opacity='0.6' clipPath={`url(#${clip1Id})`}>
              <path d='M0 20C5.33333 16.6667 10.6667 16.6667 16 20C21.3333 23.3333 26.6667 23.3333 32 20V40H0V20Z' fill='white' fillOpacity='0.5' />
              <path d='M0 25C5.33333 23 10.6667 23 16 25C21.3333 27 26.6667 27 32 25V40H0V25Z' fill='white' fillOpacity='0.35' />
              <path d='M0 30C5.33333 28.6667 10.6667 28.6667 16 30C21.3333 31.3333 26.6667 31.3333 32 30V40H0V30Z' fill='white' fillOpacity='0.2' />
            </g>
          </g>
          <defs>
            <linearGradient id={gradientId} x1='-6.9078e-07' y1='30.1987' x2='32' y2='1.80128' gradientUnits='userSpaceOnUse'>
              <stop stopColor={gradientColor1} />
              <stop offset='0.45' stopColor={gradientColor2} />
              <stop offset='1' stopColor={gradientColor3} />
            </linearGradient>
            <clipPath id={clip0Id}>
              <path d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z' fill='white' />
            </clipPath>
            <clipPath id={clip1Id}>
              <rect width='32' height='40' fill='white' />
            </clipPath>
          </defs>
        </svg>
        {icon && (
          icon === 'pause'
            ? (
              <div css={pauseBarsStyles}>
                <span />
                <span />
              </div>
            )
            : <NvIcon icon={icon} size='xss-smallest' css={iconOverlayStyles} className='text-white' />
        )}
      </div>
    </ClickableContainer>
  );
});

export default VoiceCircleColor;
